import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

/**
 * POI ingestion API — centres & stations from every source.
 *
 * Supersedes stationImport.ts, which stays in place while the old
 * /admin/stations/* screen is retired.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type PoiKind =
  | "repair_centre"
  | "inspection"
  | "fuel_station"
  | "ev_charging"
  | "tyre_centre"
  | "glass_centre"
  | "body_shop"
  | "dealer"
  | "car_wash"
  | "unknown";

export interface PoiSource {
  key: string;
  driver: string | null;
  enabled: boolean;
  trust: number | null;
  schedule: string | null;
  countries: string[];
  is_eu_wide: boolean;
  /** null = storable indefinitely. A number means a licence-imposed ceiling. */
  retention_days: number | null;
  attribution: string | null;
  requires_key: boolean;
  record_count: number;
  last_run_at: string | null;
}

export interface PoiTerritory {
  code: string;
  name: string;
  tier: "full" | "standard" | "thin";
  bbox: [number, number, number, number] | null;
  sign_count: number;
}

export interface PoiRun {
  id: string;
  source_key: string;
  status: "running" | "success" | "failed" | "aborted";
  stats: Record<string, any>;
  options: Record<string, any>;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
}

export interface PoiReviewItem {
  id: number;
  source_key: string;
  source_ref: string;
  match_rule: string | null;
  match_score: number | null;
  incoming: {
    name: string | null;
    kind: string | null;
    street: string | null;
    postal_code: string | null;
    city: string | null;
    phone: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  candidate: {
    id: number;
    name_normalized: string | null;
    latitude: number | null;
    longitude: number | null;
    kind: string | null;
    sign_id: number | null;
    sign_name: string | null;
    sign_phone: string | null;
  } | null;
  seen_at: string | null;
}

export interface PoiProvenanceField {
  field: string;
  source_key: string;
  value: any;
  trust: number;
  locked: boolean;
  attribution: string | null;
  updated_at: string | null;
}

export interface PoiStats {
  entities: number;
  signs_linked: number;
  staged_records: number;
  review_queue: number;
  rejected: number;
  missing: number;
  locked_fields: number;
  by_kind: Record<string, number>;
  by_country: Record<string, number>;
}

export interface IngestPayload {
  source: string;
  country?: string;
  path?: string;
  since?: string;
  force?: boolean;
}

export interface PoiCredentialVar {
  env_key: string;
  is_set: boolean;
  /** Masked hint (first 4 + last 4). Never the full value. */
  hint: string | null;
}

export interface PoiCredential {
  source: string;
  label: string;
  enabled: boolean;
  variables: PoiCredentialVar[];
  all_set: boolean;
  docs_url: string | null;
}

export interface UploadResult {
  fetched?: number;
  mapped?: number;
  rejected?: number;
  review_queue?: number;
  signs_created?: number;
  signs_updated?: number;
  rejections?: Record<string, number>;
  stored_as?: string;
  /** Present only for a dry run. */
  preview?: boolean;
  scanned?: number;
  unmappable?: number;
  sample?: any[];
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const poiKeys = {
  all: ["poi"] as const,
  sources: ["poi", "sources"] as const,
  territories: ["poi", "territories"] as const,
  stats: ["poi", "stats"] as const,
  runs: (source?: string, status?: string, page?: number) =>
    ["poi", "runs", source ?? "", status ?? "", page ?? 1] as const,
  run: (id: string) => ["poi", "run", id] as const,
  review: (source?: string, page?: number) =>
    ["poi", "review", source ?? "", page ?? 1] as const,
  credentials: ["poi", "credentials"] as const,
  provenance: (signId: number) => ["poi", "provenance", signId] as const,
};

// ── Registry ─────────────────────────────────────────────────────────────────

export const usePoiSources = () => {
  const { data, ...rest } = useQuery({
    queryKey: poiKeys.sources,
    queryFn: async (): Promise<PoiSource[]> => {
      const res = await axios.get("/admin/poi/sources");
      return res.data?.data ?? [];
    },
  });

  return { sources: data ?? [], ...rest };
};

export const usePoiTerritories = () => {
  const { data, ...rest } = useQuery({
    queryKey: poiKeys.territories,
    queryFn: async (): Promise<{
      countries: PoiTerritory[];
      groups: string[];
      default: string;
    }> => {
      const res = await axios.get("/admin/poi/territories");
      return res.data?.data ?? { countries: [], groups: [], default: "BE" };
    },
  });

  return {
    countries: data?.countries ?? [],
    groups: data?.groups ?? [],
    defaultCountry: data?.default ?? "BE",
    ...rest,
  };
};

export const usePoiStats = () => {
  const { data, ...rest } = useQuery({
    queryKey: poiKeys.stats,
    queryFn: async (): Promise<PoiStats> => {
      const res = await axios.get("/admin/poi/stats");
      return res.data?.data;
    },
    // Refresh while the tab is open so a run's progress is visible without a
    // manual reload, but not so often that it hammers the API.
    refetchInterval: 30_000,
  });

  return { stats: data ?? null, ...rest };
};

// ── Runs ─────────────────────────────────────────────────────────────────────

export const usePoiRuns = (params: {
  source?: string;
  status?: string;
  page?: number;
  per_page?: number;
} = {}) => {
  const { source, status, page = 1, per_page = 20 } = params;

  const { data, ...rest } = useQuery({
    queryKey: poiKeys.runs(source, status, page),
    queryFn: async (): Promise<{ data: PoiRun[]; pagination: Pagination }> => {
      const res = await axios.get("/admin/poi/runs", {
        params: { source, status, page, per_page },
      });

      return {
        data: res.data?.data ?? [],
        pagination: {
          current_page: res.data?.current_page ?? 1,
          per_page: res.data?.per_page ?? per_page,
          total: res.data?.total ?? 0,
          last_page: res.data?.last_page ?? 1,
        },
      };
    },
    // A run in flight is the one case where polling earns its keep.
    refetchInterval: (query) =>
      (query.state.data as any)?.data?.some((r: PoiRun) => r.status === "running")
        ? 5_000
        : false,
  });

  return { runs: data?.data ?? [], pagination: data?.pagination, ...rest };
};

export const usePoiRunDetail = (id: string | null) => {
  return useQuery({
    queryKey: poiKeys.run(id ?? ""),
    queryFn: async () => {
      const res = await axios.get(`/admin/poi/runs/${id}`);
      return res.data?.data;
    },
    enabled: !!id,
  });
};

/**
 * Trigger an ingestion run.
 *
 * The backend answers 422 (not 500) when a run aborts on a data condition —
 * shrink guard, circuit breaker, missing API key — because those are things
 * the operator can act on. The message is surfaced verbatim rather than
 * replaced with a generic string, since "record count fell from 5704 to 12"
 * is the entire diagnosis.
 */
export const usePoiIngest = (onSettledExtra?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IngestPayload) => {
      const res = await axios.post("/admin/poi/ingest", payload);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: poiKeys.all });
      onSettledExtra?.();

      const s = data?.data ?? {};
      toast.success(
        t("poi.ingestDone", {
          mapped: s.mapped ?? 0,
          created: s.signs_created ?? 0,
          updated: s.signs_updated ?? 0,
          defaultValue: `Import terminé — ${s.mapped ?? 0} traités, ${s.signs_created ?? 0} créés, ${s.signs_updated ?? 0} mis à jour`,
        })
      );

      if (s.review_queue) {
        toast(
          t("poi.reviewPending", {
            count: s.review_queue,
            defaultValue: `${s.review_queue} correspondance(s) à vérifier manuellement`,
          }),
          { icon: "⚠️" }
        );
      }
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          t("poi.ingestError", "Échec de l'import")
      );
    },
  });
};

// ── Review queue ─────────────────────────────────────────────────────────────

export const usePoiReview = (params: { source?: string; page?: number; per_page?: number } = {}) => {
  const { source, page = 1, per_page = 20 } = params;

  const { data, ...rest } = useQuery({
    queryKey: poiKeys.review(source, page),
    queryFn: async (): Promise<{ data: PoiReviewItem[]; pagination: Pagination }> => {
      const res = await axios.get("/admin/poi/review", {
        params: { source, page, per_page },
      });

      return {
        data: res.data?.data ?? [],
        pagination: {
          current_page: res.data?.current_page ?? 1,
          per_page: res.data?.per_page ?? per_page,
          total: res.data?.total ?? 0,
          last_page: res.data?.last_page ?? 1,
        },
      };
    },
  });

  return { items: data?.data ?? [], pagination: data?.pagination, ...rest };
};

export const useResolveReview = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      id: number;
      decision: "merge" | "separate" | "discard";
      entity_id?: number;
    }) => {
      const res = await axios.post(`/admin/poi/review/${payload.id}/resolve`, {
        decision: payload.decision,
        entity_id: payload.entity_id,
      });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: poiKeys.all });

      const labels: Record<string, string> = {
        merge: t("poi.merged", "Fusionné avec la fiche existante"),
        separate: t("poi.separated", "Créé comme fiche distincte"),
        discard: t("poi.discarded", "Écarté"),
      };

      toast.success(labels[variables.decision]);
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || t("poi.resolveError", "Échec de la résolution")
      );
    },
  });
};

// ── Credentials ──────────────────────────────────────────────────────────

/**
 * Reports which API keys are configured — never their values.
 *
 * Keys stay in .env. An admin form that writes them to the database puts a
 * Google billing credential one SQL injection away from being someone else's,
 * and makes rotation a deploy-and-migrate instead of an env change. What the
 * screen actually needs is "is it set?", which is what the backend returns.
 */
export const usePoiCredentials = () => {
  const { data, ...rest } = useQuery({
    queryKey: poiKeys.credentials,
    queryFn: async (): Promise<PoiCredential[]> => {
      const res = await axios.get("/admin/poi/credentials");
      return res.data?.data ?? [];
    },
  });

  return { credentials: data ?? [], ...rest };
};

// ── File upload ─────────────────────────────────────────────────────────

/**
 * Upload a CSV/JSON/XLSX and ingest it in the same request.
 *
 * Deliberately NOT routed through /media: those objects are tenant-scoped
 * blobs meant for later download, whereas an import file is consumed once by
 * the pipeline and kept only so a mapping bug can be replayed against the
 * exact bytes that were sent.
 */
export const usePoiUpload = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      file: File;
      source: string;
      country?: string;
      dryRun?: boolean;
      onProgress?: (percent: number) => void;
    }): Promise<UploadResult> => {
      const form = new FormData();
      form.append("file", payload.file);
      form.append("source", payload.source);
      if (payload.country) form.append("country", payload.country);
      if (payload.dryRun) form.append("dry_run", "1");

      const res = await axios.post("/admin/poi/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        // A 50 MB export over a slow uplink easily exceeds the default client
        // timeout, and the server keeps parsing regardless — so a timeout here
        // would show a false failure for an import that actually succeeded.
        timeout: 15 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) {
            payload.onProgress?.(Math.round((e.loaded * 100) / e.total));
          }
        },
      });

      return res.data?.data ?? {};
    },
    onSuccess: (data) => {
      if (data.preview) {
        return; // dry run: the panel renders the sample, no toast needed
      }

      queryClient.invalidateQueries({ queryKey: poiKeys.all });

      toast.success(
        t("poi.uploadDone", {
          mapped: data.mapped ?? 0,
          created: data.signs_created ?? 0,
          defaultValue: `Import terminé — ${data.mapped ?? 0} traités, ${data.signs_created ?? 0} créés`,
        })
      );

      if (data.rejected) {
        toast(
          t("poi.uploadRejected", {
            count: data.rejected,
            defaultValue: `${data.rejected} ligne(s) rejetée(s) — voir l'onglet Historique`,
          }),
          { icon: "⚠️" }
        );
      }
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          t("poi.uploadError", "Échec de l'import du fichier")
      );
    },
  });
};

// ── Provenance ───────────────────────────────────────────────────────────────

export const usePoiProvenance = (signId: number | null) => {
  return useQuery({
    queryKey: poiKeys.provenance(signId ?? 0),
    queryFn: async (): Promise<{
      sign_id: number;
      fields: PoiProvenanceField[];
      contributing: { source_key: string; source_ref: string; last_seen_at: string }[];
    }> => {
      const res = await axios.get(`/admin/poi/signs/${signId}/provenance`);
      return res.data?.data;
    },
    enabled: !!signId,
  });
};

/**
 * Lock a field so the pipeline stops overwriting it. This is the escape hatch
 * that makes automated ingestion safe to leave running: an operator who fixes
 * a phone number by hand can guarantee tonight's run won't revert it.
 */
export const useLockField = (signId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { field: string; locked: boolean; value?: any }) => {
      const res = await axios.post(`/admin/poi/signs/${signId}/lock`, payload);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: poiKeys.provenance(signId) });
      toast.success(
        variables.locked
          ? t("poi.fieldLocked", "Champ verrouillé — l'import ne l'écrasera plus")
          : t("poi.fieldUnlocked", "Champ déverrouillé")
      );
    },
    onError: () => {
      toast.error(t("poi.lockError", "Échec du verrouillage"));
    },
  });
};

// ── Live progress ─────────────────────────────────────────────────────

/**
 * Polls for a run currently in flight on a given source.
 *
 * The import request itself is synchronous — the browser sits on one open POST
 * for the whole run — so it can report upload bytes and nothing after that.
 * Everything interesting (parsing, matching, projection) happens server-side
 * while that request hangs, which is why the UI previously showed a spinner
 * with no way to distinguish work from a hang.
 *
 * The fix is a SECOND, parallel request: the pipeline now flushes counters to
 * poi_ingestion_run every 200 records, and this hook reads them on a short
 * interval. Same run, two connections, one of them cheap.
 */
export const useActiveRun = (sourceKey: string | null, enabled: boolean) => {
  const { data } = useQuery({
    queryKey: ["poi", "active-run", sourceKey ?? ""],
    queryFn: async (): Promise<PoiRun | null> => {
      const res = await axios.get("/admin/poi/runs", {
        params: { source: sourceKey, status: "running", per_page: 1 },
      });
      return res.data?.data?.[0] ?? null;
    },
    enabled: enabled && !!sourceKey,
    refetchInterval: 2000,
    // Progress is disposable: a stale counter is worse than no counter.
    gcTime: 0,
    staleTime: 0,
  });

  return data ?? null;
};

/**
 * Abort a stuck run and release its source lock.
 *
 * Needed because the cache lock and the run row can disagree: a request killed
 * by an nginx 504 or a PHP timeout never reaches the pipeline's `finally`, so
 * the lock outlives the process. The backend self-heals after 20 minutes;
 * this is the manual escape hatch for an operator who already knows it's dead.
 */
export const useAbortRun = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runId: string) => {
      const res = await axios.post(`/admin/poi/runs/${runId}/abort`);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: poiKeys.all });
      toast.success(data?.message ?? t("poi.aborted", "Import annulé"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ?? t("poi.abortError", "Échec de l'annulation")
      );
    },
  });
};
